import { useMemo, useState } from "react";
import {
  CalendarDays,
  CheckCircle2,
  CircleAlert,
  Clock3,
  ExternalLink,
  Inbox,
} from "lucide-react";
import {
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { useStudyHealth } from "@/api/hooks";
import type { CoverageDay } from "@/api/types";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";

const DAY_MS = 86_400_000;

function phoenixDate(offsetDays = 0) {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: "America/Phoenix",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(new Date(Date.now() + offsetDays * DAY_MS));
  const value = Object.fromEntries(parts.map((part) => [part.type, part.value]));
  return `${value.year}-${value.month}-${value.day}`;
}

function rangeForDays(days: number) {
  return { from: phoenixDate(-(days - 1)), to: phoenixDate() };
}

function offsetDate(value: string, days: number) {
  const [year, month, day] = value.split("-").map(Number);
  const next = new Date(Date.UTC(year, month - 1, day + days));
  return next.toISOString().slice(0, 10);
}

function shortDate(value: string) {
  const [, month, day] = value.split("-");
  return new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric" }).format(
    new Date(Date.UTC(2020, Number(month) - 1, Number(day))),
  );
}

function coverageCellClass(day: CoverageDay) {
  return {
    met: "bg-emerald-200 text-emerald-950 hover:bg-emerald-300",
    partial: "bg-amber-200 text-amber-950 hover:bg-amber-300",
    no_data: "bg-rose-300 text-rose-950 hover:bg-rose-400",
    na: "bg-slate-200 text-slate-500 hover:bg-slate-300",
  }[day.status];
}

export default function StudyHealthPage() {
  const initial = useMemo(() => rangeForDays(14), []);
  const [rangeDays, setRangeDays] = useState("14");
  const [range, setRange] = useState(initial);
  const [selected, setSelected] = useState<{
    participant: string;
    date: string;
    day: CoverageDay;
  } | null>(null);
  const query = useStudyHealth(range.from, range.to);
  const data = query.data?.data;

  const changeRange = (value: string | null) => {
    if (!value) return;
    setRangeDays(value);
    if (value !== "custom") setRange(rangeForDays(Number(value)));
  };

  if (query.isLoading) {
    return <div className="p-6 text-sm text-muted-foreground">Loading study health…</div>;
  }
  if (query.isError || !data) {
    return (
      <div className="p-6">
        <h1 className="text-2xl font-semibold text-[#1E293B]">Study Health</h1>
        <p className="mt-4 text-sm text-destructive">
          Unable to load study health. {query.error instanceof Error ? query.error.message : ""}
        </p>
      </div>
    );
  }

  const adherence = data.survey_adherence;
  const cards = [
    { label: "Scheduled", value: adherence.scheduled, icon: CalendarDays, color: "text-blue-600" },
    { label: "Opened", value: adherence.opened, icon: Inbox, color: "text-emerald-600" },
    { label: "Completed", value: adherence.completed, icon: CheckCircle2, color: "text-emerald-600" },
    { label: "Missed", value: adherence.missed, icon: CircleAlert, color: "text-rose-500" },
    { label: "Completion", value: `${adherence.completion_rate}%`, icon: ExternalLink, color: "text-blue-600" },
    {
      label: "Median response",
      value: adherence.median_response_minutes == null ? "—" : `${adherence.median_response_minutes} min`,
      icon: Clock3,
      color: "text-violet-500",
    },
  ];

  return (
    <div className="space-y-5 p-4 md:p-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-[#1E293B]">Study Health</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Daily collection coverage and survey adherence · {data.timezone}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {rangeDays === "custom" && (
            <>
              <input
                aria-label="Coverage start date"
                className="h-8 rounded-lg border bg-background px-2 text-sm"
                max={range.to}
                min={offsetDate(range.to, -27)}
                type="date"
                value={range.from}
                onChange={(event) => setRange((current) => ({ ...current, from: event.target.value }))}
              />
              <span className="text-xs text-muted-foreground">to</span>
              <input
                aria-label="Coverage end date"
                className="h-8 rounded-lg border bg-background px-2 text-sm"
                max={[phoenixDate(), offsetDate(range.from, 27)].sort()[0]}
                min={range.from}
                type="date"
                value={range.to}
                onChange={(event) => setRange((current) => ({ ...current, to: event.target.value }))}
              />
            </>
          )}
          <Select value={rangeDays} onValueChange={changeRange}>
            <SelectTrigger className="w-36">
              <CalendarDays className="size-4" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="7">Last 7 days</SelectItem>
              <SelectItem value="14">Last 14 days</SelectItem>
              <SelectItem value="28">Last 28 days</SelectItem>
              <SelectItem value="custom">Custom range</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <Card>
        <CardHeader>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <CardTitle>Daily Source Coverage</CardTitle>
            <div className="flex flex-wrap gap-3 text-xs text-muted-foreground">
              {[
                ["bg-emerald-400", "Met"],
                ["bg-amber-400", "Partial"],
                ["bg-rose-400", "No data"],
                ["bg-slate-300", "Not eligible / N/A"],
              ].map(([color, label]) => (
                <span className="flex items-center gap-1.5" key={label}>
                  <span className={cn("size-2.5 rounded-full", color)} />{label}
                </span>
              ))}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto pb-2">
            <div
              className="grid min-w-max gap-1"
              style={{ gridTemplateColumns: `9rem repeat(${data.dates.length}, 4.25rem)` }}
            >
              <div />
              {data.dates.map((date) => (
                <div className="pb-1 text-center text-xs font-medium text-muted-foreground" key={date}>
                  {shortDate(date)}
                </div>
              ))}
              {data.coverage.participants.map((participant) => (
                <div className="contents" key={participant.participant_id}>
                  <div className="sticky left-0 z-10 flex items-center bg-card pr-3 text-sm font-medium">
                    {participant.participant_label}
                  </div>
                  {data.dates.map((date) => {
                    const day = participant.days[date];
                    return (
                      <button
                        className={cn(
                          "h-10 rounded-md text-xs font-semibold transition-colors focus-visible:ring-2 focus-visible:ring-ring",
                          coverageCellClass(day),
                        )}
                        key={date}
                        onClick={() => setSelected({ participant: participant.participant_label, date, day })}
                        title={`${participant.participant_label}, ${date}`}
                      >
                        {day.percentage == null ? "—" : `${day.percentage}%`}
                      </button>
                    );
                  })}
                </div>
              ))}
            </div>
          </div>
          {data.coverage.participants.length === 0 && (
            <p className="py-10 text-center text-sm text-muted-foreground">No edge participants found.</p>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between gap-3">
            <CardTitle>Survey Adherence</CardTitle>
            {adherence.state_mismatches > 0 && (
              <span className="rounded-full bg-amber-100 px-3 py-1 text-xs font-medium text-amber-800">
                {adherence.state_mismatches} state mismatch{adherence.state_mismatches === 1 ? "" : "es"}
              </span>
            )}
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-6">
            {cards.map((card) => (
              <div className="rounded-lg border p-3" key={card.label}>
                <div className="flex items-center justify-between text-xs text-muted-foreground">
                  {card.label}<card.icon className={cn("size-4", card.color)} />
                </div>
                <div className="mt-2 text-2xl font-semibold text-[#1E293B]">{card.value}</div>
              </div>
            ))}
          </div>
          <div>
            <p className="mb-3 text-sm font-medium">Adherence trend</p>
            <div className="h-52 w-full">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={adherence.daily} margin={{ top: 8, right: 12, left: -20, bottom: 0 }}>
                  <XAxis dataKey="date" tickFormatter={shortDate} tick={{ fontSize: 11 }} />
                  <YAxis domain={[0, 100]} tickFormatter={(value) => `${value}%`} tick={{ fontSize: 11 }} />
                  <Tooltip labelFormatter={(value) => shortDate(String(value))} formatter={(value) => [`${value}%`, "Completion"]} />
                  <Line type="monotone" dataKey="completion_rate" stroke="#2563eb" strokeWidth={2.5} dot={{ r: 3 }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        </CardContent>
      </Card>

      <Dialog open={selected !== null} onOpenChange={(open) => !open && setSelected(null)}>
        {selected && (
          <DialogContent className="max-h-[85vh] max-w-2xl overflow-y-auto" onClose={() => setSelected(null)}>
            <DialogHeader>
              <DialogTitle>{selected.participant} · {shortDate(selected.date)}</DialogTitle>
              <DialogDescription>
                {selected.day.met_windows} of {selected.day.expected_windows} eligible 15-minute windows met
              </DialogDescription>
            </DialogHeader>
            <div className="divide-y rounded-lg border">
              {selected.day.sources.map((source) => (
                <div className="flex items-center justify-between gap-4 p-3" key={source.source}>
                  <div>
                    <p className="text-sm font-medium">{source.label}</p>
                    {!source.eligible && (
                      <p className="text-xs text-muted-foreground">
                        {source.reason === "missing_permission"
                          ? "Not eligible: required permission missing"
                          : source.reason === "not_registered"
                            ? "Participant not registered"
                            : "No elapsed windows"}
                      </p>
                    )}
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-semibold">
                      {source.percentage == null ? "—" : `${source.percentage}%`}
                    </p>
                    {source.eligible && (
                      <p className="text-xs text-muted-foreground">
                        {source.met_windows}/{source.expected_windows} windows
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </DialogContent>
        )}
      </Dialog>
    </div>
  );
}
